import {Component} from 'react';
import PropTypes from 'prop-types';
import {Dropdown, Icon, Menu} from 'semantic-ui-react';
import * as Routes from '../Routes';
import AnalyticsGateway from '../lib/AnalyticsGateway';
import {isNZLocale} from '../lib/PropertyUtils';
import {openShareEmail} from '../lib/ShareReportUtil';
import {generateShareLink} from '../api/ShareLinkApi';

export default class ShareMenu extends Component {

  static propTypes = {
    location: PropTypes.object.isRequired,
    propertyDetail: PropTypes.object.isRequired,
    reportType: PropTypes.shape({value: PropTypes.string.isRequired}).isRequired,
    reportUrl: PropTypes.string,
    featureFlags: PropTypes.shape({propertyPriceStatement: PropTypes.bool}),
  };

  getAnalyticsReportType = () => {
    const {reportType: {value}, featureFlags: {propertyPriceStatement = false} = {}} = this.props;
    return value === 'soi' && propertyPriceStatement ? 'Property Price Statement' : value;
  };

  state = {
    shareMenuOpen: false,
    linkCopied: false,
    shareUrl: null,
    shareUrlReportUrl: null,
  };

  // Plain instance field (not React state) so it is guaranteed to be up to
  // date synchronously: the "Share via Link" click handler and the
  // Dropdown's own onClose both fire within the same click event, before any
  // setState from this handler has actually been applied to this.state.
  suppressMenuClose = false;

  componentDidUpdate(prevProps) {
    const {location: {pathname: prevPathname}} = prevProps;
    const {location: {pathname}} = this.props;

    if (pathname !== prevPathname && Routes.locationInfo(pathname).pageContext !== 'Preview Report') {
      this.closeShareMenu();
    }
  }

  closeShareMenu = () => {
    this.suppressMenuClose = false;
    this.setState({shareMenuOpen: false, linkCopied: false});
  };

  onShareMenuOpen = () => this.setState({shareMenuOpen: true, linkCopied: false});

  onShareMenuClose = () => {
    if (this.suppressMenuClose) {
      this.suppressMenuClose = false;
      return;
    }
    this.setState({shareMenuOpen: false});
  };

  // Resets the tooltip so hovering "Share via Link" again shows
  // "Copy to Clipboard" rather than the stale "Copied" text.
  onShareReportLinkMouseLeave = () => {
    if (this.state.linkCopied) {
      this.setState({linkCopied: false});
    }
  };

  // Reuses the cached shareUrl for the current reportUrl (generated by either
  // "Share via Email" or "Copy Report Link", whichever runs first) so the
  // share-link API is only ever called once per report.
  getOrCreateShareUrl = async () => {
    const {reportUrl} = this.props;
    const {shareUrl: existingShareUrl, shareUrlReportUrl} = this.state;

    if (existingShareUrl && shareUrlReportUrl === reportUrl) {
      return existingShareUrl;
    }

    const shareUrl = await generateShareLink({reportUrl});
    this.setState({shareUrl, shareUrlReportUrl: reportUrl});
    return shareUrl;
  };

  // Writes to the clipboard synchronously (no leading `await`) so the call is
  // still within the click event's transient user activation. WebKit/Safari
  // ties clipboard permission to that activation, and it is consumed/expired
  // by the time an `await`ed network call (getOrCreateShareUrl) resolves,
  // causing writeText to throw NotAllowedError there. Passing the still-
  // pending shareUrl promise straight into a ClipboardItem lets the browser
  // register the write immediately and fill in the text once it resolves.
  // Falls back to writeText (after the promise resolves) for browsers
  // without ClipboardItem support, and skips entirely when the Clipboard API
  // isn't available at all, e.g. non-secure contexts.
  copyToClipboard = (textPromise) => {
    if (!navigator.clipboard) {
      return Promise.reject(new Error('Clipboard API is unavailable in this context'));
    }

    if (typeof ClipboardItem !== 'undefined' && navigator.clipboard.write) {
      const item = new ClipboardItem({
        'text/plain': textPromise.then((text) => new Blob([text], {type: 'text/plain'})),
      });
      return navigator.clipboard.write([item]);
    }

    return textPromise.then((text) => navigator.clipboard.writeText(text));
  };

  onShareReportLink = () => {
    const {
      location: {pathname},
      propertyDetail: {propertyId},
    } = this.props;
    const {pageContext} = Routes.locationInfo(pathname);

    // Keep the dropdown menu open for this item only, so the "Copied" tooltip
    // remains visible instead of the menu closing on click like other items.
    // Set synchronously (instance field, not state) since the Dropdown's own
    // onClose fires immediately after this click handler, within the same event.
    this.suppressMenuClose = true;

    const shareUrlPromise = this.getOrCreateShareUrl();

    this.copyToClipboard(shareUrlPromise)
      .then(() => {
        this.setState({linkCopied: true});

        AnalyticsGateway.sendEvent({
          eventName: 'Copy Report Link',
          properties: [
            {label: 'Page', value: pageContext},
            {label: 'Property ID', value: propertyId},
            {label: 'Report Type', value: this.getAnalyticsReportType()},
          ],
        });
      })
      .catch((e) => {
        console.error('Failed to copy the report link to the clipboard', e);
      });
  };

  onShareReportEmail = async () => {
    const {
      location: {pathname},
      propertyDetail: {propertyId, singleLineAddress},
      reportType: {value: reportTypeValue},
      featureFlags: {propertyPriceStatement = false} = {},
    } = this.props;
    const {pageContext} = Routes.locationInfo(pathname);

    try {
      const shareUrl = await this.getOrCreateShareUrl();
      openShareEmail({
        reportType: reportTypeValue,
        propertyAddress: singleLineAddress,
        reportLink: shareUrl,
        site: isNZLocale() ? 'Property Guru' : 'RP Data',
        propertyPriceStatement,
      });

      AnalyticsGateway.sendEvent({
        eventName: 'Share Report',
        properties: [
          {label: 'Page', value: pageContext},
          {label: 'Property ID', value: propertyId},
          {label: 'Report Type', value: this.getAnalyticsReportType()},
        ],
      });
    } catch (e) {
      console.error('Failed to generate the share link for email', e);
    }
  };

  render() {
    const {location: {pathname}, reportUrl} = this.props;
    const locationInfo = Routes.locationInfo(pathname);

    if (locationInfo.pageContext !== 'Preview Report') {
      return null;
    }

    const {shareMenuOpen, linkCopied} = this.state;

    return (
      <Menu.Item className="share-report-menu">
        <Dropdown
          className="icon primary share-button"
          text="Share"
          icon="dropdown"
          floating
          labeled
          button
          upward
          disabled={!reportUrl}
          open={shareMenuOpen}
          onOpen={this.onShareMenuOpen}
          onClose={this.onShareMenuClose}
        >
          <Dropdown.Menu direction="left">
            <Dropdown.Item
              className="share-via-email"
              icon={<Icon name="mail outline" className="share-menu-icon" />}
              text="Share via Email"
              description="Send the report link by email"
              onClick={this.onShareReportEmail}
            />
            <div className="share-report-link-wrapper" onMouseLeave={this.onShareReportLinkMouseLeave}>
              <Dropdown.Item
                className="share-report-link"
                icon={<Icon name="share alternate" className="share-menu-icon" />}
                text="Share via Link"
                description="Copy the report link"
                onClick={this.onShareReportLink}
              />
              {/*
                A lightweight, CSS-only tooltip (rather than Semantic UI's Popup) so
                "Copy to Clipboard" and "Copied" can each size themselves independently
                without drifting away from this item: Popup only calculates its pixel
                position once, when it first mounts, so swapping to shorter/longer text
                afterwards leaves it anchored using stale dimensions.
              */}
              <div className={`copy-link-tooltip${linkCopied ? ' copied' : ''}`}>
                {linkCopied ? 'Copied' : 'Copy to Clipboard'}
              </div>
            </div>
          </Dropdown.Menu>
        </Dropdown>
      </Menu.Item>
    );
  }
}
